import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Box, Tabs, Tab, TextField, FormControl, InputLabel, Select,
  MenuItem, SelectChangeEvent, Switch, Snackbar, Alert,
} from '@mui/material';
import api from '../../api/client';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} role="tabpanel">
    {value === index && <div className="pt-6">{children}</div>}
  </div>
);

const VOICE_OPTIONS = [
  { value: 'en-US-Claire', label: 'Claire (English US)' },
  { value: 'en-US-Jordan', label: 'Jordan (English US)' },
  { value: 'en-GB-Ryan', label: 'Ryan (English UK)' },
  { value: 'en-AU-Natalie', label: 'Natalie (English AU)' },
  { value: 'es-ES-Pablo', label: 'Pablo (Spanish ES)' },
  { value: 'fr-FR-Denise', label: 'Denise (French FR)' },
  { value: 'ja-JP-Nanami', label: 'Nanami (Japanese)' },
];

interface AgentForm {
  agent_name: string;
  description: string;
  primary_language: string;
  supported_languages: string[];
  voice_id: string;
  personality_prompt: string;
  greeting_message: string;
  fallback_message: string;
  escalation_message: string;
  status: string;
}

export const AgentEdit: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm] = useState<AgentForm>({
    agent_name: '',
    description: '',
    primary_language: 'en-US',
    supported_languages: ['en-US'],
    voice_id: 'en-US-Claire',
    personality_prompt: '',
    greeting_message: '',
    fallback_message: '',
    escalation_message: '',
    status: 'draft',
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/agents/${agentId}`);
      const a = res.data.agent;
      setForm({
        agent_name: a.agent_name || '',
        description: a.description || '',
        primary_language: a.primary_language || 'en-US',
        supported_languages: a.supported_languages || [a.primary_language || 'en-US'],
        voice_id: a.voice_id || 'en-US-Claire',
        personality_prompt: a.personality_prompt || '',
        greeting_message: a.greeting_message || '',
        fallback_message: a.fallback_message || '',
        escalation_message: a.escalation_message || '',
        status: a.status || 'draft',
      });
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.put(`/api/v1/agents/${agentId}`, form);
      setSaveSuccess(true);
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading agent...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/agents" className="hover:text-gray-300 transition-colors text-gray-500 no-underline">
          Agents
        </Link>
        <span>›</span>
        <span className="text-gray-300">{form.agent_name || 'Edit Agent'}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">{form.agent_name || 'Edit Agent'}</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/agents')}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-60"
            style={{ background: '#6366f1' }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {saveError && (
        <div
          className="rounded-lg px-4 py-3 mb-6 text-sm text-red-300"
          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          {saveError}
        </div>
      )}

      {/* Card */}
      <div
        className="rounded-xl"
        style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            sx={{ px: 2, '& .MuiTabs-indicator': { backgroundColor: '#6366f1' } }}
          >
            <Tab label="General" />
            <Tab label="Voice & Language" />
            <Tab label="Behaviour" />
          </Tabs>
        </Box>

        <div className="p-6">
          {/* General tab */}
          <TabPanel value={tabIndex} index={0}>
            <div className="flex flex-col gap-5">
              <TextField
                label="Agent Name"
                value={form.agent_name}
                onChange={(e) => setForm({ ...form, agent_name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
                placeholder="Briefly describe what this agent does..."
              />
              <div className="flex items-center gap-4 pt-1">
                <span className="text-sm text-gray-300 font-medium">Status</span>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-sm ${form.status !== 'active' ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    Draft
                  </span>
                  <Switch
                    checked={form.status === 'active'}
                    onChange={(e) => setForm({ ...form, status: e.target.checked ? 'active' : 'draft' })}
                    sx={{
                      '& .MuiSwitch-thumb': { backgroundColor: form.status === 'active' ? '#6366f1' : '#6b7280' },
                      '& .MuiSwitch-track': { backgroundColor: form.status === 'active' ? 'rgba(99,102,241,0.4)' : 'rgba(107,114,128,0.3)' },
                    }}
                  />
                  <span className={`text-sm ${form.status === 'active' ? 'text-emerald-400' : 'text-gray-500'}`}>
                    Active
                  </span>
                </div>
              </div>
            </div>
          </TabPanel>

          {/* Voice & Language tab */}
          <TabPanel value={tabIndex} index={1}>
            <div className="flex flex-col gap-5">
              <FormControl fullWidth>
                <InputLabel>Voice</InputLabel>
                <Select
                  value={form.voice_id}
                  label="Voice"
                  onChange={(e: SelectChangeEvent) => setForm({ ...form, voice_id: e.target.value })}
                >
                  {VOICE_OPTIONS.map((v) => (
                    <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Primary Language"
                value={form.primary_language}
                onChange={(e) => setForm({
                  ...form,
                  primary_language: e.target.value,
                  supported_languages: [e.target.value],
                })}
                fullWidth
                placeholder="e.g. en-US"
                helperText="BCP-47 language tag (e.g. en-US, ja-JP, fr-FR)"
              />
            </div>
          </TabPanel>

          {/* Behaviour tab */}
          <TabPanel value={tabIndex} index={2}>
            <div className="flex flex-col gap-5">
              <div>
                <TextField
                  label="Personality Prompt"
                  value={form.personality_prompt}
                  onChange={(e) => setForm({ ...form, personality_prompt: e.target.value })}
                  multiline
                  rows={5}
                  fullWidth
                  placeholder="You are a helpful customer support agent for [Company]. Be friendly and professional..."
                  inputProps={{ maxLength: 4000 }}
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {form.personality_prompt.length} / 4000
                </div>
              </div>
              <TextField
                label="Greeting Message"
                value={form.greeting_message}
                onChange={(e) => setForm({ ...form, greeting_message: e.target.value })}
                fullWidth
                placeholder="Hello! How can I help you today?"
              />
              <TextField
                label="Fallback Message"
                value={form.fallback_message}
                onChange={(e) => setForm({ ...form, fallback_message: e.target.value })}
                fullWidth
                placeholder="Sorry, I didn't understand. Could you please repeat that?"
              />
              <TextField
                label="Escalation Message"
                value={form.escalation_message}
                onChange={(e) => setForm({ ...form, escalation_message: e.target.value })}
                fullWidth
                placeholder="Let me connect you with a human agent who can better assist you."
              />
            </div>
          </TabPanel>
        </div>
      </div>

      {/* Success toast */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)} sx={{ width: '100%' }}>
          Agent saved successfully!
        </Alert>
      </Snackbar>
    </div>
  );
};
